import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { createUser } from "../../api/usersService";
import { addInstructor, fetchInstructors } from "../../store/instructorsSlice";
import { addCar, fetchCars, removeCar, updateCar } from "../../store/carsSlice";
import { fetchUsers } from "../../store/usersSlice";
import { UserContext } from "../../UserContext";
import { closePopup as closePopupUI } from "../Common/popupUIStore";
import DistrictsDatalist from "../Common/DistrictsDatalist";

const ALL_ROLE_OPTIONS = [
   {
      value: "INSTRUCTOR",
      label: "Instructor",
   },
   {
      value: "ADMIN",
      label: "Admin",
   },
   {
      value: "MANAGER",
      label: "Manager",
   },
   {
      value: "PROFESSOR",
      label: "Profesor",
   },
];

function normalizeRole(value, options) {
   const role = String(value || "").trim().toUpperCase();
   return options.some((option) => option.value === role) ? role : null;
}

function getDefaultRole(initialRole, options) {
   return normalizeRole(initialRole, options) || options[0]?.value || "INSTRUCTOR";
}

function getRoleLabel(role, options) {
   return options.find((option) => option.value === role)?.label || "Personal";
}

function buildInitialForm() {
   return {
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      password: "",
      confirmPassword: "",
      sector: "",
      carPlate: "",
      gearbox: "manual",
   };
}

function normPlate(value) {
   return String(value || "")
      .replace(/\s+/g, "")
      .replace(/-/g, "")
      .toUpperCase();
}

function toApiGearbox(value) {
   return String(value || "")
      .toLowerCase()
      .includes("auto")
      ? "automat"
      : "manual";
}

export default function PersonalCreateUI({ initialRole = null }) {
   const dispatch = useDispatch();
   const { user } = useContext(UserContext);
   const { list: instructors = [], status: instructorsStatus = "idle" } =
      useSelector((state) => state.instructors || {});
   const { list: cars = [], status: carsStatus = "idle" } = useSelector(
      (state) => state.cars || {},
   );

   const roleOptions = useMemo(() => {
      const isAdmin =
         String(user?.role || "")
            .trim()
            .toUpperCase() === "ADMIN";

      if (isAdmin) return ALL_ROLE_OPTIONS;

      return ALL_ROLE_OPTIONS.filter((option) => option.value !== "ADMIN");
   }, [user?.role]);

   const selectedRole = useMemo(
      () => getDefaultRole(initialRole, roleOptions),
      [initialRole, roleOptions],
   );

   const selectedRoleLabel = getRoleLabel(selectedRole, roleOptions);
   const [form, setForm] = useState(() => buildInitialForm());
   const [submitting, setSubmitting] = useState(false);
   const [error, setError] = useState("");

   useEffect(() => {
      if (selectedRole !== "INSTRUCTOR") return;
      if (instructorsStatus === "idle") dispatch(fetchInstructors());
      if (carsStatus === "idle") dispatch(fetchCars());
   }, [carsStatus, dispatch, instructorsStatus, selectedRole]);

   const instructorsById = useMemo(() => {
      const map = new Map();

      for (const item of instructors || []) {
         const id = String(item?.id ?? "").trim();
         if (!id) continue;
         map.set(id, item);
      }

      return map;
   }, [instructors]);

   const carsByPlate = useMemo(() => {
      const map = new Map();

      for (const car of cars || []) {
         const plate = normPlate(car?.plateNumber);
         if (!plate) continue;
         map.set(plate, car);
      }

      return map;
   }, [cars]);

   const carsByInstructorId = useMemo(() => {
      const map = new Map();

      for (const car of cars || []) {
         const id = String(car?.instructorId ?? "").trim();
         if (!id) continue;
         map.set(id, car);
      }

      return map;
   }, [cars]);

   const normalizedCarPlate = useMemo(
      () => normPlate(form.carPlate),
      [form.carPlate],
   );

   const duplicateCar = useMemo(() => {
      if (selectedRole !== "INSTRUCTOR" || !normalizedCarPlate) return null;
      return carsByPlate.get(normalizedCarPlate) || null;
   }, [carsByPlate, normalizedCarPlate, selectedRole]);

   const duplicateOwner = useMemo(() => {
      const ownerId = String(duplicateCar?.instructorId ?? "").trim();
      if (!ownerId) return null;
      return instructorsById.get(ownerId) || null;
   }, [duplicateCar, instructorsById]);

   const duplicateOwnerLabel = useMemo(() => {
      if (!duplicateOwner) return "";

      const fullName =
         `${duplicateOwner.firstName || ""} ${
            duplicateOwner.lastName || ""
         }`.trim();

      if (fullName) return fullName;
      return `Instructor #${duplicateOwner.id}`;
   }, [duplicateOwner]);

   const updateFormValue = (field, value) => {
      setForm((current) => ({
         ...current,
         [field]: value,
      }));
   };

   const upsertCarForInstructor = async ({ instructorId, plate, gearbox }) => {
      const normalizedPlate = normPlate(plate);
      const existing = carsByInstructorId.get(String(instructorId));

      if (!normalizedPlate) {
         if (existing) {
            await dispatch(removeCar(existing.id)).unwrap();
         }
         return;
      }

      const duplicate = carsByPlate.get(normalizedPlate);

      if (duplicate && String(duplicate?.instructorId ?? "") !== String(instructorId)) {
         // mașina trece la acest instructor; mașina lui de până acum o șterge serverul
         await dispatch(
            updateCar({
               id: duplicate.id,
               plateNumber: duplicate.plateNumber,
               instructorId,
               gearbox: toApiGearbox(gearbox),
            }),
         ).unwrap();

         return;
      }

      const payload = {
         plateNumber: String(plate || "").trim(),
         instructorId,
         gearbox: toApiGearbox(gearbox),
      };

      if (existing) {
         await dispatch(updateCar({ id: existing.id, ...payload })).unwrap();
      } else {
         await dispatch(addCar(payload)).unwrap();
      }
   };

   const validateForm = () => {
      if (!form.firstName.trim() || !form.lastName.trim()) {
         return "Completează numele și prenumele.";
      }
      if (!form.email.trim()) {
         return "Email este obligatoriu.";
      }
      if (!form.password || form.password.length < 6) {
         return "Parola trebuie să aibă minim 6 caractere.";
      }
      if (form.password !== form.confirmPassword) {
         return "Parolele nu coincid.";
      }
      return "";
   };

   const handleSubmit = async (event) => {
      event.preventDefault();

      const validationError = validateForm();
      if (validationError) {
         setError(validationError);
         return;
      }

      setSubmitting(true);
      setError("");

      try {
         if (selectedRole === "INSTRUCTOR") {
            const createdInstructor = await dispatch(
               addInstructor({
                  firstName: form.firstName.trim(),
                  lastName: form.lastName.trim(),
                  phone: form.phone.trim(),
                  email: form.email.trim(),
                  password: form.password,
                  sector: form.sector.trim(),
                  isActive: true,
               }),
            ).unwrap();

            const createdInstructorId = Number(createdInstructor?.id);

            if (createdInstructorId > 0 && normalizedCarPlate) {
               await upsertCarForInstructor({
                  instructorId: createdInstructorId,
                  plate: form.carPlate,
                  gearbox: form.gearbox,
               });
            }

            await Promise.all([
               dispatch(fetchInstructors()),
               dispatch(fetchUsers()),
               dispatch(fetchCars()),
            ]);
         } else {
            await createUser({
               firstName: form.firstName.trim(),
               lastName: form.lastName.trim(),
               phone: form.phone.trim(),
               email: form.email.trim(),
               password: form.password,
               role: selectedRole,
            });

            dispatch(fetchUsers());
         }

         closePopupUI();
      } catch (submissionError) {
         setError(
            submissionError?.message ||
               "Nu am putut salva noul element de personal.",
         );
      } finally {
         setSubmitting(false);
      }
   };

   return (
      <section className="personalCreateUI">
         <div className="personalCreateUI__header">
            <h2 className="personalCreateUI__title">
               {selectedRoleLabel} nou
            </h2>
         </div>

         <form className="personalCreateUI__content" onSubmit={handleSubmit}>
            <section className="personalCreateUI__panel">
               <div className="personalCreateUI__grid">
                  <label className="personalCreateUI__field">
                     <span className="personalCreateUI__fieldLabel">Prenume</span>
                     <input
                        className="personalCreateUI__input"
                        value={form.firstName}
                        onChange={(event) =>
                           updateFormValue("firstName", event.target.value)
                        }
                        autoComplete="given-name"
                     />
                  </label>

                  <label className="personalCreateUI__field">
                     <span className="personalCreateUI__fieldLabel">Nume</span>
                     <input
                        className="personalCreateUI__input"
                        value={form.lastName}
                        onChange={(event) =>
                           updateFormValue("lastName", event.target.value)
                        }
                        autoComplete="family-name"
                     />
                  </label>

                  <label className="personalCreateUI__field">
                     <span className="personalCreateUI__fieldLabel">Telefon</span>
                     <input
                        className="personalCreateUI__input"
                        value={form.phone}
                        onChange={(event) =>
                           updateFormValue("phone", event.target.value)
                        }
                        autoComplete="tel"
                     />
                  </label>

                  <label className="personalCreateUI__field">
                     <span className="personalCreateUI__fieldLabel">Email</span>
                     <input
                        type="email"
                        className="personalCreateUI__input"
                        value={form.email}
                        onChange={(event) =>
                           updateFormValue("email", event.target.value)
                        }
                        autoComplete="email"
                     />
                  </label>

                  <label className="personalCreateUI__field">
                     <span className="personalCreateUI__fieldLabel">Parolă</span>
                     <input
                        type="password"
                        className="personalCreateUI__input"
                        value={form.password}
                        onChange={(event) =>
                           updateFormValue("password", event.target.value)
                        }
                        autoComplete="new-password"
                     />
                  </label>

                  <label className="personalCreateUI__field">
                     <span className="personalCreateUI__fieldLabel">
                        Confirmă parola
                     </span>
                     <input
                        type="password"
                        className="personalCreateUI__input"
                        value={form.confirmPassword}
                        onChange={(event) =>
                           updateFormValue("confirmPassword", event.target.value)
                        }
                        autoComplete="new-password"
                     />
                  </label>

                  {selectedRole === "INSTRUCTOR" && (
                     <label className="personalCreateUI__field">
                        <span className="personalCreateUI__fieldLabel">Sector</span>
                        <input
                           className="personalCreateUI__input"
                           value={form.sector}
                           onChange={(event) =>
                              updateFormValue("sector", event.target.value)
                           }
                           placeholder="Ex: Botanica"
                           list="personal-create-districts"
                        />
                        <DistrictsDatalist id="personal-create-districts" />
                     </label>
                  )}


                  {selectedRole === "INSTRUCTOR" && (
                     <label className="personalCreateUI__field">
                        <span className="personalCreateUI__fieldLabel">
                           Nr. mașină
                        </span>
                        <input
                           className="personalCreateUI__input"
                           value={form.carPlate}
                           onChange={(event) =>
                              updateFormValue("carPlate", event.target.value)
                           }
                           placeholder="Ex: ABC 123"
                           autoComplete="off"
                        />
                     </label>
                  )}

                  {selectedRole === "INSTRUCTOR" && (
                     <div className="personalCreateUI__selectorCard personalCreateUI__field--full">
                        <span className="personalCreateUI__fieldLabel">
                           Cutie
                        </span>

                        <div
                           className={`personalCreateUI__radioWrapper personalCreateUI__radioWrapper--${
                              form.gearbox === "manual" ? "left" : "right"
                           }`}
                        >
                           <label className="personalCreateUI__radioOption">
                              <input
                                 type="radio"
                                 name="gearbox"
                                 value="manual"
                                 checked={form.gearbox === "manual"}
                                 onChange={(event) =>
                                    updateFormValue("gearbox", event.target.value)
                                 }
                              />
                              <span>Manual</span>
                           </label>

                           <label className="personalCreateUI__radioOption">
                              <input
                                 type="radio"
                                 name="gearbox"
                                 value="automat"
                                 checked={form.gearbox === "automat"}
                                 onChange={(event) =>
                                    updateFormValue("gearbox", event.target.value)
                                 }
                              />
                              <span>Automat</span>
                           </label>
                        </div>
                     </div>
                  )}
               </div>
            </section>

            {selectedRole === "INSTRUCTOR" && duplicateCar && (
               <div className="personalCreateUI__stateCard personalCreateUI__stateCard--info">
                  {duplicateOwnerLabel
                     ? `Mașina ${duplicateCar.plateNumber} este atribuită lui ${duplicateOwnerLabel}. La salvare va fi reatribuită automat instructorului nou.`
                     : `Mașina ${duplicateCar.plateNumber} există deja în sistem. La salvare va fi conectată automat instructorului nou.`}
               </div>
            )}

            {error ? (
               <div className="personalCreateUI__stateCard personalCreateUI__stateCard--error">
                  {error}
               </div>
            ) : null}

            <div className="personalCreateUI__footer">
               <button
                  type="button"
                  className="personalCreateUI__button personalCreateUI__button--secondary"
                  onClick={closePopupUI}
                  disabled={submitting}
               >
                  Renunță
               </button>

               <button
                  type="submit"
                  className="personalCreateUI__button personalCreateUI__button--primary"
                  disabled={submitting}
               >
                  {submitting
                     ? "Se salvează..."
                     : `Creează ${selectedRoleLabel.toLowerCase()}`}
               </button>
            </div>
         </form>
      </section>
   );
}
